import { lazy, Suspense, useEffect, useRef } from "react";
  import { ErrorBoundary } from "@/components/ErrorBoundary";
  import { ClerkProvider, SignIn, SignUp, useClerk, useUser, useAuth, useSession } from "@clerk/react";
  import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
  import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
  import { Toaster } from "@/components/ui/toaster";
  import { DevSentryTest } from "@/components/DevSentryTest";
  import { TooltipProvider } from "@/components/ui/tooltip";
  import { Loader2 } from "lucide-react";
  import { setAuthTokenGetter, setWorkspaceIdGetter, setActiveWorkspaceSink } from "@workspace/api-client-react";
  import { setApiTokenGetter, setApiWorkspaceIdGetter } from "@/lib/api-fetch";
  import { getActiveWorkspaceId, setActiveWorkspaceId } from "@/lib/active-workspace";
  import { resolveClerkLandingState } from "@/lib/clerk-session-state";
  import { PostHogLifecycle } from "@/components/PostHogLifecycle";

  // Public pages — eagerly imported for fast crawler/unauthenticated-user path
  import { BookingPageWrapper } from "./pages/BookingPage";
  import { ClientPortalPage } from "./pages/ClientPortal";
  import { InviteAccept } from "./pages/InviteAccept";
  import { Privacy } from "./pages/Privacy";
  import { HelpCenter } from "./pages/HelpCenter";
  import { Terms } from "./pages/Terms";
  import { PublicSecurity } from "./pages/PublicSecurity";
  import { FieldManual } from "./pages/FieldManual";
  import { MissionDebrief } from "./pages/MissionDebrief";
  import { PublicLanding } from "./pages/PublicLanding";
  import { CategoryGuide } from "./pages/CategoryGuide";
  import NotFound from "@/pages/not-found";

  // Authenticated app pages — lazy-loaded to keep the initial JS bundle below 2 MB
  const Dashboard = lazy(() => import("./pages/Dashboard").then(m => ({ default: m.Dashboard })));
  const Profile = lazy(() => import("./pages/Profile").then(m => ({ default: m.Profile })));
  const Outreach = lazy(() => import("./pages/Outreach").then(m => ({ default: m.Outreach })));
  const Deals = lazy(() => import("./pages/Deals").then(m => ({ default: m.Deals })));
  const Social = lazy(() => import("./pages/Social").then(m => ({ default: m.Social })));
  const SEO = lazy(() => import("./pages/SEO").then(m => ({ default: m.SEO })));
  const Network = lazy(() => import("./pages/Network").then(m => ({ default: m.Network })));
  const Settings = lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
  const Inbox = lazy(() => import("./pages/Inbox").then(m => ({ default: m.Inbox })));
  const Listening = lazy(() => import("./pages/Listening").then(m => ({ default: m.Listening })));
  const ABTesting = lazy(() => import("./pages/ABTesting").then(m => ({ default: m.ABTesting })));
  const Reviews = lazy(() => import("./pages/Reviews").then(m => ({ default: m.Reviews })));
  const Intelligence = lazy(() => import("./pages/Intelligence").then(m => ({ default: m.Intelligence })));
  const Reports = lazy(() => import("./pages/Reports").then(m => ({ default: m.Reports })));
  const SMS = lazy(() => import("./pages/SMS").then(m => ({ default: m.SMS })));
  const Scheduling = lazy(() => import("./pages/Scheduling").then(m => ({ default: m.Scheduling })));
  const Webhooks = lazy(() => import("./pages/Webhooks").then(m => ({ default: m.Webhooks })));
  const Deliverability = lazy(() => import("./pages/Deliverability").then(m => ({ default: m.Deliverability })));
  const Team = lazy(() => import("./pages/Team").then(m => ({ default: m.Team })));
  const Billing = lazy(() => import("./pages/Billing").then(m => ({ default: m.Billing })));
  const BillingSuccess = lazy(() => import("./pages/BillingSuccess").then(m => ({ default: m.BillingSuccess })));
  const WorkspaceSuccess = lazy(() => import("./pages/WorkspaceSuccess").then(m => ({ default: m.WorkspaceSuccess })));
  const Pricing = lazy(() => import("./pages/Pricing").then(m => ({ default: m.Pricing })));
  const ProposalsPage = lazy(() => import("./pages/Proposals").then(m => ({ default: m.ProposalsPage })));
  const ChatWidgetPage = lazy(() => import("./pages/ChatWidget").then(m => ({ default: m.ChatWidgetPage })));
  const WhiteLabelPage = lazy(() => import("./pages/WhiteLabel").then(m => ({ default: m.WhiteLabelPage })));
  const JarvisPairingApproval = lazy(() => import("./pages/JarvisPairingApproval").then(m => ({ default: m.JarvisPairingApproval })));
  const Agents = lazy(() => import("./pages/Agents").then(m => ({ default: m.Agents })));
  const AgentDetail = lazy(() => import("./pages/AgentDetail").then(m => ({ default: m.AgentDetail })));
  const Workflows = lazy(() => import("./pages/Workflows").then(m => ({ default: m.Workflows })));
  const WorkflowEditor = lazy(() => import("./pages/WorkflowEditor").then(m => ({ default: m.WorkflowEditor })));
  const HotAccounts = lazy(() => import("./pages/HotAccounts").then(m => ({ default: m.HotAccounts })));
  const CompanyList = lazy(() => import("./pages/CompanyList").then(m => ({ default: m.CompanyList })));
  const CompanyIntelligence = lazy(() => import("./pages/CompanyIntelligence").then(m => ({ default: m.CompanyIntelligence })));
  const SignalFeed = lazy(() => import("./pages/SignalFeed").then(m => ({ default: m.SignalFeed })));
  const DataSources = lazy(() => import("./pages/DataSources").then(m => ({ default: m.DataSources })));
  const AdminDashboard = lazy(() => import("./pages/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
  const PlatformHealthCentre = lazy(() => import("./pages/PlatformHealthCentre").then(m => ({ default: m.PlatformHealthCentre })));
  const ProductHealthPage = lazy(() => import("./pages/PlatformHealthCentre").then(m => ({ default: m.ProductHealthPage })));
  const AdminExplorium = lazy(() => import("./pages/AdminExplorium"));
  const BusinessProfilesPage = lazy(() => import("./pages/BusinessProfiles").then(m => ({ default: m.BusinessProfilesPage })));
  const IntelDeliverabilityMonitor = lazy(() => import("./pages/IntelDeliverabilityMonitor").then(m => ({ default: m.IntelDeliverabilityMonitor })));
  const DataQuality = lazy(() => import("./pages/DataQuality").then(m => ({ default: m.DataQuality })));
  const Actions = lazy(() => import("./pages/Actions").then(m => ({ default: m.Actions })));
  const AutonomousSettings = lazy(() => import("./pages/AutonomousSettings").then(m => ({ default: m.AutonomousSettings })));
  const ComplianceDashboard = lazy(() => import("./pages/ComplianceDashboard").then(m => ({ default: m.ComplianceDashboard })));
  const AuditLog = lazy(() => import("./pages/AuditLog").then(m => ({ default: m.AuditLog })));
  const UnsubscribeManager = lazy(() => import("./pages/UnsubscribeManager").then(m => ({ default: m.UnsubscribeManager })));
  const BounceLog = lazy(() => import("./pages/BounceLog").then(m => ({ default: m.BounceLog })));
  const SuppressionListPage = lazy(() => import("./pages/SuppressionListPage").then(m => ({ default: m.SuppressionListPage })));
  const SOC2Controls = lazy(() => import("./pages/SOC2Controls").then(m => ({ default: m.SOC2Controls })));
  const ISO27001Controls = lazy(() => import("./pages/ISO27001Controls").then(m => ({ default: m.ISO27001Controls })));
  const SecurityDocs = lazy(() => import("./pages/SecurityDocs").then(m => ({ default: m.SecurityDocs })));
  const ComplianceSettings = lazy(() => import("./pages/ComplianceSettings").then(m => ({ default: m.ComplianceSettings })));
  const ActionLogs = lazy(() => import("./pages/ActionLogs").then(m => ({ default: m.ActionLogs })));
  const AssetRegister = lazy(() => import("./pages/AssetRegister").then(m => ({ default: m.AssetRegister })));
  const VendorRegister = lazy(() => import("./pages/VendorRegister").then(m => ({ default: m.VendorRegister })));
  const RiskRegister = lazy(() => import("./pages/RiskRegister").then(m => ({ default: m.RiskRegister })));
  const Outcomes = lazy(() => import("./pages/Outcomes").then(m => ({ default: m.Outcomes })));
  const AccountIntelligence = lazy(() => import("./pages/AccountIntelligence").then(m => ({ default: m.AccountIntelligence })));
  const ReplyIntelligence = lazy(() => import("./pages/ReplyIntelligence").then(m => ({ default: m.ReplyIntelligence })));
  const MeetingIntelligence = lazy(() => import("./pages/MeetingIntelligence").then(m => ({ default: m.MeetingIntelligence })));
  const ContactDatabase = lazy(() => import("./pages/ContactDatabase").then(m => ({ default: m.ContactDatabase })));
  const CompanyDatabase = lazy(() => import("./pages/CompanyDatabase").then(m => ({ default: m.CompanyDatabase })));
  const LeadGenAgencyWorkflow = lazy(() => import("./pages/LeadGenAgencyWorkflow").then(m => ({ default: m.LeadGenAgencyWorkflow })));
  const PipelineAnalytics = lazy(() => import("./pages/PipelineAnalytics").then(m => ({ default: m.PipelineAnalytics })));
  const OperationalAccuracyDashboard = lazy(() => import("./pages/OperationalAccuracyDashboard").then(m => ({ default: m.OperationalAccuracyDashboard })));
  const SequenceOptimizer = lazy(() => import("./pages/SequenceOptimizer").then(m => ({ default: m.SequenceOptimizer })));
  const WorkspaceAdmin = lazy(() => import("./pages/WorkspaceAdmin").then(m => ({ default: m.WorkspaceAdmin })));
  const JarvisInstallations = lazy(() => import("./pages/JarvisInstallations").then(m => ({ default: m.JarvisInstallations })));
  const JarvisPair = lazy(() => import("./pages/JarvisPair").then(m => ({ default: m.JarvisPair })));
  
  import { useIsAdmin } from "./hooks/use-is-admin";
  import { useIsOwner } from "./hooks/use-is-owner";
  import { useIsPlatformAdmin } from "./hooks/use-is-platform-admin";

  const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

  function stripBase(path: string): string {
    return basePath && path.startsWith(basePath)
      ? path.slice(basePath.length) || "/"
      : path;
  }

  const clerkAppearance = {
    variables: {
      colorPrimary: "#7C6EEF",
      colorBackground: "#0D1520",
      colorInputBackground: "#111827",
      colorForeground: "#ffffff",
      colorMutedForeground: "rgba(255,255,255,0.55)",
      colorNeutral: "#374151",
      colorDanger: "#EF4444",
      borderRadius: "0.375rem",
      fontFamily: "Inter, sans-serif",
      fontSize: "0.9375rem",
      colorInput: "#111827",
      colorInputForeground: "#ffffff",
    },
    elements: {
      card: "bg-[#0D1520] border border-white/10 shadow-2xl",
      headerTitle: "text-white font-bold tracking-tight",
      headerSubtitle: "text-white/50",
      formButtonPrimary:
        "bg-[#7C6EEF] hover:bg-[#6A5ED4] text-white font-semibold",
      formFieldInput:
        "bg-[#111827] border-white/10 text-white placeholder:text-white/30 focus:border-[#7C6EEF]",
      formFieldLabel: "text-white/70 font-medium",
      footerActionLink: "text-[#7C6EEF] hover:text-[#9E8FFF]",
      footerActionText: "text-white/50",
      identityPreviewText: "text-white",
      identityPreviewEditButton: "text-[#7C6EEF]",
      dividerLine: "bg-white/10",
      dividerText: "text-white/30",
      socialButtonsBlockButton: "border-white/10 text-white hover:bg-white/5",
      socialButtonsBlockButtonText: "text-white",
      alertText: "text-white/80",
      formResendCodeLink: "text-[#7C6EEF]",
      logoBox: { display: "none" },
    },
  };

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (
            error &&
            typeof error === "object" &&
            "status" in error &&
            (error as { status: number }).status === 401
          ) {
            return failureCount < 1;
          }
          return false;
        },
        retryDelay: 800,
        refetchOnWindowFocus: false,
      },
    },
  });

  function ClerkTokenInitializer() {
    const { getToken } = useAuth();

    setAuthTokenGetter(() => getToken());
    setApiTokenGetter(() => getToken());
    setWorkspaceIdGetter(() => getActiveWorkspaceId());
    setApiWorkspaceIdGetter(() => getActiveWorkspaceId());
    setActiveWorkspaceSink((id) => setActiveWorkspaceId(id));

    useEffect(() => {
      return () => {
        setAuthTokenGetter(null);
        setApiTokenGetter(null);
        setWorkspaceIdGetter(null);
        setApiWorkspaceIdGetter(null);
        setActiveWorkspaceSink(null);
      };
    }, []);
    return null;
  }

  function ClerkQueryClientCacheInvalidator() {
    const { addListener } = useClerk();
    const qc = useQueryClient();
    const prevUserIdRef = useRef<string | null | undefined>(undefined);

    useEffect(() => {
      const unsubscribe = addListener(({ user }) => {
        const userId = user?.id ?? null;
        if (
          prevUserIdRef.current !== undefined &&
          prevUserIdRef.current !== userId
        ) {
          qc.clear();
        }
        prevUserIdRef.current = userId;
      });
      return unsubscribe;
    }, [addListener, qc]);

    return null;
  }

  function SignInPage() {
    const params = new URLSearchParams(window.location.search);
    const rawRedirect = params.get("redirect_url");
    // Only permit local relative paths — prevents open-redirect to external domains.
    const redirectUrl =
      rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//")
        ? rawRedirect
        : null;
    return (
      <div className="min-h-screen bg-[#030913] flex flex-col items-center justify-center px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-[#7C6EEF]/5 via-transparent to-transparent pointer-events-none" />
        <div className="relative z-10 w-full max-w-md flex flex-col items-center">
          <img
            src={`${import.meta.env.BASE_URL}images/logo-wordmark.png`}
            alt="LaunchTactix"
            width={606}
            height={216}
            className="h-10 w-auto mb-6 mix-blend-screen"
          />
          <SignIn
            routing="path"
            path={`${basePath}/sign-in`}
            signUpUrl={`${basePath}/sign-up`}
            fallbackRedirectUrl={redirectUrl ?? `${basePath}/`}
            appearance={clerkAppearance}
          />
        </div>
      </div>
    );
  }

  function SignUpPage() {
    return (
      <div className="min-h-screen bg-[#030913] flex flex-col items-center justify-center px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-[#7C6EEF]/5 via-transparent to-transparent pointer-events-none" />
        <div className="relative z-10 w-full max-w-md flex flex-col items-center">
          <img
            src={`${import.meta.env.BASE_URL}images/logo-wordmark.png`}
            alt="LaunchTactix"
            width={606}
            height={216}
            className="h-10 w-auto mb-6 mix-blend-screen"
          />
          <SignUp
            routing="path"
            path={`${basePath}/sign-up`}
            signInUrl={`${basePath}/sign-in`}
            fallbackRedirectUrl={`${basePath}/`}
            appearance={clerkAppearance}
          />
        </div>
      </div>
    );
  }

  function Loading() {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  /**
   * Redirects an unauthenticated visitor to the sign-in page, preserving the
   * attempted path as `redirect_url` so Clerk can return them after login.
   * Shared by all authenticated route wrappers — do not duplicate this logic.
   */
  function SignedOutRedirect() {
    useEffect(() => {
      const returnTo = window.location.pathname + window.location.search;
      window.location.replace(
        `${basePath}/sign-in?redirect_url=${encodeURIComponent(returnTo)}`
      );
    }, []);
    return <Loading />;
  }

  function LandingRoute() {
    const { isLoaded, isSignedIn } = useAuth({
      treatPendingAsSignedOut: false,
    });
    const { session } = useSession();
    const authState = resolveClerkLandingState({
      authIsLoaded: isLoaded,
      authIsSignedIn: isSignedIn,
      sessionStatus: session?.status,
    });

    if (authState === "loading") return <Loading />;
    if (authState === "landing") return <PublicLanding />;
    return <Dashboard />;
  }

  function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
    const { isLoaded, isSignedIn } = useUser();
    if (!isLoaded) return <Loading />;
    if (!isSignedIn) return <SignedOutRedirect />;
    return <Component />;
  }

  function ProtectedAgentDetail({ params }: { params: { id: string } }) {
    const { isLoaded, isSignedIn } = useUser();
    if (!isLoaded) return <Loading />;
    if (!isSignedIn) return <SignedOutRedirect />;
    return <AgentDetail params={params} />;
  }

  function ProtectedWorkflowEditor({ params }: { params: { id: string } }) {
    const { isLoaded, isSignedIn } = useUser();
    if (!isLoaded) return <Loading />;
    if (!isSignedIn) return <SignedOutRedirect />;
    return <WorkflowEditor params={params} />;
  }

  function ProtectedCompanyIntelligence({ params }: { params: { id: string } }) {
    const { isLoaded, isSignedIn } = useUser();
    if (!isLoaded) return <Loading />;
    if (!isSignedIn) return <SignedOutRedirect />;
    return <CompanyIntelligence params={params} />;
  }

  function AdminProtectedRoute({ component: Component }: { component: React.ComponentType }) {
    const { isLoaded, isSignedIn } = useUser();
    const { isAdmin, isLoading: adminLoading } = useIsAdmin();

    if (!isLoaded) return <Loading />;
    if (!isSignedIn) return <SignedOutRedirect />;
    if (adminLoading) return <Loading />;
    if (!isAdmin) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-sm px-6">
            <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">Access Restricted</h2>
            <p className="text-sm text-muted-foreground">This area is only accessible to workspace owners and admins.</p>
          </div>
        </div>
      );
    }
    return <Component />;
  }

  function OwnerProtectedRoute({ component: Component }: { component: React.ComponentType }) {
    const { isLoaded, isSignedIn } = useUser();
    const { isOwner, isLoading: ownerLoading } = useIsOwner();

    if (!isLoaded) return <Loading />;
    if (!isSignedIn) return <SignedOutRedirect />;
    if (ownerLoading) return <Loading />;
    if (!isOwner) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-sm px-6">
            <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">Owner Access Required</h2>
            <p className="text-sm text-muted-foreground">This area is only accessible to the workspace owner.</p>
          </div>
        </div>
      );
    }
    return <Component />;
  }

  function JarvisOwnerRoute() {
    const { isLoaded, isSignedIn } = useUser();
    const { isOwner, isLoading: ownerLoading } = useIsOwner();

    if (!isLoaded) return <Loading />;
    if (!isSignedIn) return <SignedOutRedirect />;
    if (ownerLoading) return <Loading />;

    if (!isOwner) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-sm px-6">
            <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">Owner Access Required</h2>
            <p className="text-sm text-muted-foreground">Only the workspace owner can approve Jarvis pairing requests.</p>
          </div>
        </div>
      );
    }

    return <JarvisPairingApproval />;
  }

  function PlatformAdminProtectedRoute({ component: Component }: { component: React.ComponentType }) {
    const { isLoaded, isSignedIn } = useUser();
    const { isPlatformAdmin, isLoading } = useIsPlatformAdmin();

    if (!isLoaded) return <Loading />;
    if (!isSignedIn) return <SignedOutRedirect />;
    if (isLoading) return <Loading />;
    if (!isPlatformAdmin) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center max-w-sm px-6">
            <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">Platform Admin Only</h2>
            <p className="text-sm text-muted-foreground">This area is restricted to the platform administrator. Contact the platform owner if you need access.</p>
          </div>
        </div>
      );
    }
    return <Component />;
  }

  function GaPageTracker() {
    const [location] = useLocation();
    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (typeof (window as any).gtag === "function") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).gtag("event", "page_view", { page_path: location });
      }
    }, [location]);
    return null;
  }

  function Router() {
    return (
      <>
        <GaPageTracker />
        <Suspense fallback={<Loading />}>
        <Switch>
          <Route path="/">
            <LandingRoute />
          </Route>
        <Route path="/sign-in/*?" component={SignInPage} />
        <Route path="/sign-up/*?" component={SignUpPage} />
        <Route path="/profile"><ProtectedRoute component={Profile} /></Route>
        <Route path="/outreach"><ProtectedRoute component={Outreach} /></Route>
        <Route path="/deals"><ProtectedRoute component={Deals} /></Route>
        <Route path="/social"><ProtectedRoute component={Social} /></Route>
        <Route path="/seo"><ProtectedRoute component={SEO} /></Route>
        <Route path="/network"><ProtectedRoute component={Network} /></Route>
        <Route path="/settings"><ProtectedRoute component={Settings} /></Route>
        <Route path="/inbox"><ProtectedRoute component={Inbox} /></Route>
        <Route path="/listening"><ProtectedRoute component={Listening} /></Route>
        <Route path="/abtesting"><ProtectedRoute component={ABTesting} /></Route>
        <Route path="/reviews"><ProtectedRoute component={Reviews} /></Route>
        <Route path="/intelligence/companies"><ProtectedRoute component={CompanyList} /></Route>
        <Route path="/intelligence/hot-accounts"><ProtectedRoute component={HotAccounts} /></Route>
        <Route path="/intelligence/signals"><ProtectedRoute component={SignalFeed} /></Route>
        <Route path="/intelligence/data-sources"><PlatformAdminProtectedRoute component={DataSources} /></Route>
        <Route path="/intelligence"><ProtectedRoute component={Intelligence} /></Route>
        <Route path="/workspace-admin"><AdminProtectedRoute component={WorkspaceAdmin} /></Route>
        <Route path="/admin"><PlatformAdminProtectedRoute component={AdminDashboard} /></Route>
        <Route path="/admin/product-health"><PlatformAdminProtectedRoute component={ProductHealthPage} /></Route>
        <Route path="/admin/health"><PlatformAdminProtectedRoute component={PlatformHealthCentre} /></Route>
        <Route path="/admin/explorium"><PlatformAdminProtectedRoute component={AdminExplorium} /></Route>
        <Route path="/admin/jarvis"><PlatformAdminProtectedRoute component={JarvisInstallations} /></Route>
        <Route path="/jarvis/pair"><PlatformAdminProtectedRoute component={JarvisPair} /></Route>
        <Route path="/business-profiles"><ProtectedRoute component={BusinessProfilesPage} /></Route>
        <Route path="/intelligence/deliverability"><ProtectedRoute component={IntelDeliverabilityMonitor} /></Route>
        <Route path="/intelligence/data-quality"><ProtectedRoute component={DataQuality} /></Route>
        <Route path="/actions"><ProtectedRoute component={Actions} /></Route>
        <Route path="/settings/autonomous"><ProtectedRoute component={AutonomousSettings} /></Route>
        <Route path="/settings/integrations/jarvis"><JarvisOwnerRoute /></Route>
        <Route path="/intelligence/company/:id">{(params: { id: string }) => <ProtectedCompanyIntelligence params={params as { id: string }} />}</Route>
        <Route path="/reports"><ProtectedRoute component={Reports} /></Route>
        <Route path="/sms"><ProtectedRoute component={SMS} /></Route>
        <Route path="/scheduling"><ProtectedRoute component={Scheduling} /></Route>
        <Route path="/webhooks"><ProtectedRoute component={Webhooks} /></Route>
        <Route path="/deliverability"><ProtectedRoute component={Deliverability} /></Route>
        <Route path="/team"><ProtectedRoute component={Team} /></Route>
        <Route path="/pricing"><ProtectedRoute component={Pricing} /></Route>
        <Route path="/billing/success"><ProtectedRoute component={BillingSuccess} /></Route>
        <Route path="/billing/workspace-success"><ProtectedRoute component={WorkspaceSuccess} /></Route>
        <Route path="/billing"><ProtectedRoute component={Billing} /></Route>
        <Route path="/compliance"><AdminProtectedRoute component={ComplianceDashboard} /></Route>
        <Route path="/compliance/audit-log"><AdminProtectedRoute component={AuditLog} /></Route>
        <Route path="/compliance/unsubscribes"><AdminProtectedRoute component={UnsubscribeManager} /></Route>
        <Route path="/compliance/bounce-log"><AdminProtectedRoute component={BounceLog} /></Route>
        <Route path="/compliance/suppression"><AdminProtectedRoute component={SuppressionListPage} /></Route>
        <Route path="/compliance/soc2"><AdminProtectedRoute component={SOC2Controls} /></Route>
        <Route path="/compliance/iso27001"><AdminProtectedRoute component={ISO27001Controls} /></Route>
        <Route path="/compliance/security-docs"><AdminProtectedRoute component={SecurityDocs} /></Route>
        <Route path="/compliance/settings"><OwnerProtectedRoute component={ComplianceSettings} /></Route>
        <Route path="/compliance/action-logs"><AdminProtectedRoute component={ActionLogs} /></Route>
        <Route path="/compliance/asset-register"><AdminProtectedRoute component={AssetRegister} /></Route>
        <Route path="/compliance/vendor-register"><AdminProtectedRoute component={VendorRegister} /></Route>
        <Route path="/compliance/risk-register"><AdminProtectedRoute component={RiskRegister} /></Route>
        <Route path="/outcomes"><ProtectedRoute component={Outcomes} /></Route>
        <Route path="/pipeline-analytics"><ProtectedRoute component={PipelineAnalytics} /></Route>
        <Route path="/accuracy"><ProtectedRoute component={OperationalAccuracyDashboard} /></Route>
        <Route path="/sequence-optimizer/:id">{(params: { id: string }) => <ProtectedRoute component={() => <SequenceOptimizer params={params as { id: string }} />} />}</Route>
        <Route path="/growth-intelligence"><ProtectedRoute component={AccountIntelligence} /></Route>
        <Route path="/reply-intelligence"><ProtectedRoute component={ReplyIntelligence} /></Route>
        <Route path="/meeting-intelligence"><ProtectedRoute component={MeetingIntelligence} /></Route>
        <Route path="/database/contacts"><ProtectedRoute component={ContactDatabase} /></Route>
        <Route path="/database/companies"><ProtectedRoute component={CompanyDatabase} /></Route>
        <Route path="/workflows/lead-gen-agency"><ProtectedRoute component={LeadGenAgencyWorkflow} /></Route>
        <Route path="/proposals"><ProtectedRoute component={ProposalsPage} /></Route>
        <Route path="/chat-widgets"><ProtectedRoute component={ChatWidgetPage} /></Route>
        <Route path="/white-label"><ProtectedRoute component={WhiteLabelPage} /></Route>
        <Route path="/agents/:id">{(params: { id: string }) => <ProtectedAgentDetail params={params as { id: string }} />}</Route>
        <Route path="/agents"><ProtectedRoute component={Agents} /></Route>
        <Route path="/workflows/:id/editor">{(params: { id: string }) => <ProtectedWorkflowEditor params={params as { id: string }} />}</Route>
        <Route path="/workflows"><ProtectedRoute component={Workflows} /></Route>
        <Route path="/portal/:portalToken">{(params: { [key: string]: string | undefined }) => <ClientPortalPage params={params} />}</Route>
        <Route path="/book/:slug">{(params: { [key: string]: string | undefined }) => <BookingPageWrapper params={params} />}</Route>
        <Route path="/invite/:token"><InviteAccept /></Route>
        <Route path="/privacy"><Privacy /></Route>
        <Route path="/terms"><Terms /></Route>
        <Route path="/security"><PublicSecurity /></Route>
        <Route path="/help"><HelpCenter /></Route>
        <Route path="/field-manual"><FieldManual /></Route>
        <Route path="/mission-debrief"><MissionDebrief /></Route>
        <Route path="/what-is-autonomous-ai-growth-platform"><CategoryGuide /></Route>
        <Route component={NotFound} />
        </Switch>
        </Suspense>
      </>
    );
  }

  function ClerkProviderWithRoutes() {
    const [, setLocation] = useLocation();

    if (!clerkPubKey) {
      return (
        <div className="min-h-screen bg-[#030913] flex items-center justify-center text-white/50 text-sm font-mono">
          Auth configuration loading…
        </div>
      );
    }

    return (
      <ClerkProvider
        publishableKey={clerkPubKey}
        routerPush={(to) => setLocation(stripBase(to))}
        routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
        signInUrl={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
        signInFallbackRedirectUrl={`${basePath}/`}
        signUpFallbackRedirectUrl={`${basePath}/`}
        appearance={clerkAppearance}
      >
        <QueryClientProvider client={queryClient}>
          <ClerkTokenInitializer />
          <ClerkQueryClientCacheInvalidator />
          <PostHogLifecycle />
          <Router />
        </QueryClientProvider>
      </ClerkProvider>
    );
  }

  function App() {
    return (
      <ErrorBoundary>
        <TooltipProvider>
          <WouterRouter base={basePath}>
            <ClerkProviderWithRoutes />
          </WouterRouter>
          <Toaster />
          <DevSentryTest />
        </TooltipProvider>
      </ErrorBoundary>
    );
  }

  export default App;
  